import { useState, useEffect } from 'react'
import api from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { MessageSquare, TrendingUp } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

interface Question {
  question: string
  frequency: number
  uniqueConversations: number
  category: string
}

interface Category {
  category: string
  count: number
  questions: Question[]
}

interface CommonQuestionsData {
  questions: Question[]
  categories: Category[]
  totalAnalyzed: number
}

interface CommonQuestionsProps {
  timeframe: string
}

export default function CommonQuestions({ timeframe }: CommonQuestionsProps) {
  const [data, setData] = useState<CommonQuestionsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [view, setView] = useState<'questions' | 'categories'>('categories')

  useEffect(() => {
    loadData()
  }, [timeframe])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const response = await api.get(`/api/analytics/common-questions?timeframe=${timeframe}&limit=15`)
      if (response.data.success) {
        setData(response.data.commonQuestions)
      }
    } catch (error) {
      console.error('Failed to load common questions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      pricing: 'bg-green-100 text-green-800',
      schedule: 'bg-blue-100 text-blue-800',
      hours: 'bg-purple-100 text-purple-800',
      help: 'bg-orange-100 text-orange-800',
      demo: 'bg-pink-100 text-pink-800',
      features: 'bg-indigo-100 text-indigo-800',
      contact: 'bg-cyan-100 text-cyan-800',
      refund: 'bg-red-100 text-red-800',
      shipping: 'bg-yellow-100 text-yellow-800',
      account: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200',
      other: 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200'
    }
    return colors[category] || colors.other
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Common Questions</CardTitle>
          <CardDescription>Most frequently asked questions by visitors</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.questions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Common Questions</CardTitle>
          <CardDescription>Most frequently asked questions by visitors</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No questions data available for this time period
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Common Questions
            </CardTitle>
            <CardDescription>
              Top {data.questions.length} questions from {data.totalAnalyzed} unique messages
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setView('categories')}
              className={`px-3 py-1 rounded text-sm ${
                view === 'categories'
                  ? 'bg-brand-primary text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              By Category
            </button>
            <button
              onClick={() => setView('questions')}
              className={`px-3 py-1 rounded text-sm ${
                view === 'questions'
                  ? 'bg-brand-primary text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All Questions
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {view === 'categories' ? (
          <div className="space-y-4">
            {data.categories.slice(0, 8).map((cat, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className={getCategoryColor(cat.category)}>
                      {cat.category.charAt(0).toUpperCase() + cat.category.slice(1)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {cat.count} {cat.count === 1 ? 'question' : 'questions'}
                    </span>
                  </div>
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  {cat.questions.slice(0, 3).map((q, qi) => (
                    <div key={qi} className="text-sm bg-muted/50 rounded p-2">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-foreground line-clamp-2 flex-1">{q.question}</p>
                        <span className="text-brand-primary font-medium shrink-0">×{q.frequency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {data.questions.map((q, index) => (
              <div key={index} className="border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">{q.question}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <Badge className={getCategoryColor(q.category)} variant="outline">
                        {q.category}
                      </Badge>
                      <span>{q.uniqueConversations} conversations</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-bold text-brand-primary">{q.frequency}</div>
                    <div className="text-xs text-muted-foreground">times asked</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
